import { Client } from '@twilio/conversations';
import { Dispatch } from 'redux';
import log from 'loglevel';
import { notifications } from '../notifications';
import { addNotification, changeEngagementPhase } from './genericActions';
import { ACTION_START_SESSION, ACTION_ADD_PARTICIPANT_EVENT_MESSAGE } from './actionTypes';
import { EngagementPhase } from './definitions';
import { initClientListeners, initConversationListener, initMessagesListener, initParticipantsListener } from './listeners';
import { MESSAGES_LOAD_COUNT } from '../../constants';

export function initSession({ token, conversationSid }: { token: string; conversationSid: string }) {
  return async (dispatch: Dispatch) => {
    console.log('🔄 CHAT SESSION INITIALIZATION STARTED:', {
      conversationSid: conversationSid,
      hasToken: !!token,
      timestamp: new Date().toISOString()
    });

    let conversationsClient: Client;
    let conversation;
    let participants;
    let users;
    let messages;

    try {
      conversationsClient = await Client.create(token);
      console.log('📱 TWILIO CLIENT CREATED SUCCESSFULLY');

      try {
        conversation = await conversationsClient.getConversationBySid(conversationSid);
        console.log('💬 CONVERSATION LOADED:', {
          conversationSid: conversation.sid,
          state: (conversation as any).state?.current || 'unknown',
          uniqueName: conversation.uniqueName
        });
      } catch (e) {
        console.error('❌ FAILED TO LOAD CONVERSATION:', {
          conversationSid: conversationSid,
          error: e
        });
        dispatch(addNotification(notifications.failedToInitSessionNotification("Couldn't load conversation")));
        dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
        return;
      }

      participants = await conversation.getParticipants();
      console.log('👥 PARTICIPANTS LOADED:', {
        count: participants.length,
        participants: participants.map((p: any) => ({
          sid: p.sid,
          identity: p.identity,
          type: p.type
        }))
      });

      users = await Promise.all(participants.map(async (p: any) => {
        try {
          if ((p as any)?.type === 'chat') {
            return await p.getUser();
          }
        } catch {}
        return { identity: (p as any)?.identity } as any;
      }));

      messages = (await conversation.getMessages(MESSAGES_LOAD_COUNT)).items;
      console.log('📨 INITIAL MESSAGES LOADED:', {
        count: messages.length,
        messages: messages.map((m: any) => ({
          sid: m.sid,
          author: m.author,
          body: m.body?.substring(0, 50) + (m.body?.length > 50 ? '...' : ''),
          timestamp: m.dateCreated,
          hasMedia: m.attachedMedia?.length > 0
        }))
      });
    } catch (e) {
      console.error('❌ SESSION INITIALIZATION FAILED:', e);
      log.error('Something went wrong when initializing session', e);
      throw e;
    }

    console.log('🎯 DISPATCHING SESSION START:', {
      conversationSid: conversationSid,
      participantCount: participants.length,
      messageCount: messages.length,
      conversationState: (conversation as any).state?.current,
      phase: EngagementPhase.MessagingCanvas
    });

    dispatch({
      type: ACTION_START_SESSION,
      payload: {
        token,
        conversationSid,
        conversationsClient,
        conversation,
        users,
        participants,
        messages,
        conversationState: (conversation as any).state?.current,
        currentPhase: EngagementPhase.MessagingCanvas,
      },
    });

    console.log('🔗 INITIALIZING EVENT LISTENERS');
    initClientListeners(conversationsClient, dispatch, undefined, conversation);
    initConversationListener(conversation, dispatch);
    await initMessagesListener(conversation, dispatch);
    initParticipantsListener(conversation, dispatch);

    // Show initial join messages for existing participants (especially the user)
    console.log('👋 CHECKING FOR INITIAL PARTICIPANT JOIN MESSAGES');
    for (let i = 0; i < participants.length; i++) {
      const participant = participants[i];
      const user = users[i];

      console.log('🔍 PROCESSING INITIAL PARTICIPANT:', {
        participantSid: participant.sid,
        identity: (participant as any)?.identity,
        type: (participant as any)?.type,
        userFriendlyName: user?.friendlyName,
        userIdentity: user?.identity
      });

      // Create join message for each participant (especially the user who just joined)
      // Import the createParticipantEventMessage function
      const { createParticipantEventMessage } = await import('./listeners');
      const eventMessage = await createParticipantEventMessage(participant, 'joined', user, conversationSid);
      if (eventMessage) {
        console.log('✅ DISPATCHING INITIAL JOIN MESSAGE:', eventMessage.body);
        dispatch({ type: ACTION_ADD_PARTICIPANT_EVENT_MESSAGE, payload: { eventMessage } });
      }
    }

    console.log('✅ CHAT SESSION INITIALIZATION COMPLETED SUCCESSFULLY');
  };
}

